import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Container } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FiAlertCircle, FiArrowLeft, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

import Logo from '../components/common/Logo';
import StatCards from '../components/admin/StatCards';
import AppointmentFilters from '../components/admin/AppointmentFilters';
import AppointmentsTable from '../components/admin/AppointmentsTable';
import AppointmentDetail from '../components/admin/AppointmentDetail';
import TherapistWorkload from '../components/admin/TherapistWorkload';
import ApplicationsPanel from '../components/admin/ApplicationsPanel';
import { useAuth } from '../context/AuthContext';
import {
  getAdminAppointments,
  getAdminFilters,
  getAdminSummary,
} from '../api/client';

const EMPTY_QUERY = {
  q: '',
  status: 'all',
  modality: 'all',
  therapistId: '',
  from: '',
  to: '',
  sort: 'scheduledFor',
  order: 'desc',
  page: 1,
  pageSize: 12,
};

function Pagination({ pagination, onPage }) {
  const { page, pages, total, pageSize } = pagination;
  if (total === 0) return null;

  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);

  return (
    <div className="mm-pagination">
      <span className="mm-pagination__info">
        Showing <strong>{first}</strong>&ndash;<strong>{last}</strong> of{' '}
        <strong>{total}</strong>
      </span>

      <div className="mm-pagination__controls">
        <button
          type="button"
          className="mm-icon-btn"
          onClick={() => onPage(page - 1)}
          disabled={page <= 1}
          aria-label="Previous page"
        >
          <FiChevronLeft size={16} aria-hidden="true" />
        </button>
        <span className="mm-pagination__page">
          Page {page} of {pages}
        </span>
        <button
          type="button"
          className="mm-icon-btn"
          onClick={() => onPage(page + 1)}
          disabled={page >= pages}
          aria-label="Next page"
        >
          <FiChevronRight size={16} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();

  const [view, setView] = useState('appointments');
  const [query, setQuery] = useState(EMPTY_QUERY);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pages: 1,
    total: 0,
    pageSize: 12,
  });
  const [summary, setSummary] = useState(null);
  const [therapists, setTherapists] = useState([]);
  const [options, setOptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);

  // Guards against a slow earlier request landing after a faster later one and
  // overwriting the table with stale rows.
  const requestId = useRef(0);

  // -- Summary + filter options: fetched once ------------------------------
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const [summaryResult, filterResult] = await Promise.all([
          getAdminSummary(),
          getAdminFilters(),
        ]);
        if (cancelled) return;
        setSummary(summaryResult.summary);
        setTherapists(summaryResult.therapists);
        setOptions(filterResult);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setSummaryLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // -- Appointments: refetched whenever the query changes ------------------
  const loadAppointments = useCallback(async (activeQuery) => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);

    try {
      const result = await getAdminAppointments(activeQuery);
      if (id !== requestId.current) return; // superseded
      setRows(result.appointments);
      setPagination(result.pagination);
    } catch (err) {
      if (id !== requestId.current) return;
      setError(
        err.status === 403
          ? 'Your account no longer has admin access.'
          : err.message || 'Could not load appointments.'
      );
      setRows([]);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, []);

  // Debounced so typing in the search box doesn't fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => loadAppointments(query), 250);
    return () => clearTimeout(timer);
  }, [query, loadAppointments]);

  // Any filter change resets to page 1 — staying on page 7 of a now-3-page
  // result set shows an empty table.
  const handleFilterChange = (patch) =>
    setQuery((prev) => ({ ...prev, ...patch, page: 1 }));

  const handleSort = (sort, order) =>
    setQuery((prev) => ({ ...prev, sort, order, page: 1 }));

  const handleUpdated = (updated) => {
    setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    setSelected(updated);
    // Counts and revenue have moved, so pull a fresh summary.
    getAdminSummary()
      .then((result) => {
        setSummary(result.summary);
        setTherapists(result.therapists);
      })
      .catch(() => {
        /* The table is already correct; a stale stat row is survivable. */
      });
  };

  return (
    <div className="mm-admin">
      <header className="mm-admin__bar">
        <Container fluid className="mm-admin__bar-inner">
          <Link to="/" className="mm-admin__brand" aria-label="MindlyFriend home">
            <Logo markSize={32} />
            <span className="mm-admin__tag">Admin</span>
          </Link>

          <div className="mm-admin__bar-right">
            <span className="mm-admin__user">
              Signed in as <strong>{user?.email}</strong>
            </span>
            <Link to="/" className="mm-auth__back">
              <FiArrowLeft size={15} aria-hidden="true" />
              Back to site
            </Link>
            <Button variant="outline-ink" size="sm" onClick={logout}>
              Log out
            </Button>
          </div>
        </Container>
      </header>

      <Container fluid className="mm-admin__body">
        <div className="mm-admin__intro">
          <h1 className="mm-h2">{view === 'appointments' ? 'Appointments' : 'Applications'}</h1>
          <p className="mm-lead mb-0">
            {view === 'appointments'
              ? 'Every booking on the platform — which client is seeing which therapist, when, and how it ended.'
              : 'Therapists waiting to join. Approving one lists them publicly and creates their portal account.'}
          </p>
        </div>

        <div className="mm-portal__tabs mb-4" role="tablist" aria-label="Dashboard section">
          {[
            { key: 'appointments', label: 'Appointments' },
            { key: 'applications', label: 'Applications' },
          ].map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={view === t.key}
              className={`mm-portal__tab ${view === t.key ? 'mm-portal__tab--active' : ''}`}
              onClick={() => setView(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {view === 'applications' && <ApplicationsPanel />}

        {view === 'appointments' && (
          <>
        <StatCards summary={summary} loading={summaryLoading} />

        {error && (
          <div className="mm-form-alert mm-form-alert--error" role="alert">
            <FiAlertCircle size={16} aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        <div className="mm-admin__grid">
          <div className="mm-admin__main">
            <AppointmentFilters
              query={query}
              options={options}
              onChange={handleFilterChange}
              onReset={() => setQuery(EMPTY_QUERY)}
              resultCount={pagination.total}
              loading={loading}
            />

            <AppointmentsTable
              appointments={rows}
              loading={loading}
              sort={query.sort}
              order={query.order}
              onSort={handleSort}
              onSelect={setSelected}
              selectedId={selected?.id}
            />

            <Pagination
              pagination={pagination}
              onPage={(page) => setQuery((prev) => ({ ...prev, page }))}
            />
          </div>

          <aside className="mm-admin__side">
            <TherapistWorkload
              therapists={therapists}
              loading={summaryLoading}
              activeId={query.therapistId}
              onSelect={(therapistId) =>
                handleFilterChange({ therapistId: String(therapistId) })
              }
            />
          </aside>
        </div>
          </>
        )}
      </Container>

      <AppointmentDetail
        appointment={selected}
        onClose={() => setSelected(null)}
        onUpdated={handleUpdated}
      />
    </div>
  );
}
